import { Dayjs } from "dayjs";
import i18next from "i18next";
import { TemplateResult } from "lit-html";
import {
	Rule,
	Message,
	MessageResult,
	AbstractValidation,
	FluentValidatorMethodsPartial,
	Messages,
	Validator,
} from "./ValidationTypes";
import { createDateAfterRule, createDateSameOrAfterRule, createDateBeforeRule, createDateSameOrBeforeRule, createDateBetweenInclusiveRule } from "./dateRules";
import { createIsNumberRule, createLengthRule, createMaxRule, createMinRule } from "./numericRules";
import { createNotEmptyRule, createIsInRule, createIsNotInRule } from "./collectionRules";
import { createRequiredRule, createEmailRule, createMatchRule, createNotMatchRule } from "./stringRules";

export type { Validator, Messages, MessageResult, AbstractValidation, FluentValidatorMethodsPartial };

export class Validation<FN extends string, VM> {
	private field: FN = undefined as any;
	private selector: (model: VM) => unknown = undefined as any;
	private rules: Rule[] = [];
	private validator: Validation<string, unknown>[] = [];
	private condition: (model: VM) => boolean = (_) => true;
	private arrayIdSelector?: (vm: any) => number;

	private constructor() {}

	public static ruleFor = <FN2 extends string, VM2>(field: FN2, selector: (model: VM2) => unknown) => {
		const v = new Validation<FN2, VM2>();
		v.field = field;
		v.selector = selector;
		return v;
	};

	public isRequired = (message?: string, scope: "form" | "field" = "form", validateImmediately?: boolean) => {
		this.rules.push(createRequiredRule(message, scope, validateImmediately));
		return this;
	};

	public hasLength = (length: number, message?: string, validateImmediately?: boolean) => {
		this.rules.push(createLengthRule(length, message, validateImmediately));
		return this;
	};

	public isNumber = (message?: string, validateImmediately?: boolean) => {
		this.rules.push(createIsNumberRule(message, validateImmediately));
		return this;
	};

	public isDateAfter = (date: Dayjs, message?: string, validateImmediately?: boolean) => {
		this.rules.push(createDateAfterRule(date, message, validateImmediately));
		return this;
	};

	public isDateSameOrAfter = (date: Dayjs, message?: string, validateImmediately?: boolean) => {
		this.rules.push(createDateSameOrAfterRule(date, message, validateImmediately));
		return this;
	};

	public isDateBefore = (date: Dayjs, message?: string, validateImmediately?: boolean) => {
		this.rules.push(createDateBeforeRule(date, message, validateImmediately));
		return this;
	};

	public isDateSameOrBefore = (date: Dayjs, message?: string, validateImmediately?: boolean) => {
		this.rules.push(createDateSameOrBeforeRule(date, message, validateImmediately));
		return this;
	};

	public isDateBetweenInclusive = (from: Dayjs, to: Dayjs, message?: string, validateImmediately?: boolean) => {
		this.rules.push(createDateBetweenInclusiveRule(from, to, message, validateImmediately));
		return this;
	};

	public max = (max: number, message?: string, validateImmediately?: boolean) => {
		this.rules.push(createMaxRule(max, message, validateImmediately));
		return this;
	};

	public min = (min: number, message?: string, validateImmediately?: boolean) => {
		this.rules.push(createMinRule(min, message, validateImmediately));
		return this;
	};

	public notEmpty = (message?: string, validateImmediately?: boolean) => {
		this.rules.push(createNotEmptyRule(message, validateImmediately));
		return this;
	};

	public isIn = (list: any[], message?: string, validateImmediately?: boolean) => {
		this.rules.push(createIsInRule(list, message, validateImmediately));
		return this;
	};

	public isNotIn = (list: any[], message?: string, validateImmediately?: boolean) => {
		this.rules.push(createIsNotInRule(list, message, validateImmediately));
		return this;
	};

	public fulfilsValidations = <FN2 extends string, VM2>(
		validator: Validation<FN2, VM2>[],
		arrayIdSelector?: (vm: VM2) => number
	) => {
		this.validator = validator as Validation<string, unknown>[];
		this.arrayIdSelector = arrayIdSelector;
		return this;
	};

	public fulfils = (
		condition: (val: any) => Promise<boolean>,
		message: string | TemplateResult,
		scope: "form" | "field" = "field",
		validateImmediately?: boolean
	) => {
		const rule: Rule = {
			message: { text: message, scope, validateImmediately },
			type: "condition",
			validate: condition,
		};
		this.rules.push(rule);
		return this;
	};

	public confirmsFields = (field1: keyof VM, field2: keyof VM) => {
		const rule: Rule = {
			message: {
				text: i18next.t("Los valores no coinciden."),
				scope: "field",
				validateImmediately: false,
			},
			type: "condition",
			validate: async (_, model: VM) => model[field1] === model[field2],
		};
		this.rules.push(rule);
		return this;
	};

	public when = (condition: (model: VM) => boolean) => {
		this.condition = condition;
		return this;
	};

	public isEmail = (message?: string, validateImmediately?: boolean) => {
		this.rules.push(createEmailRule(message, validateImmediately));
		return this;
	};

	public isMatch = (regex: RegExp, message?: string, validateImmediately?: boolean) => {
		this.rules.push(createMatchRule(regex, message, validateImmediately));
		return this;
	};

	public isNotMatch = (regex: RegExp, message?: string, validateImmediately?: boolean) => {
		this.rules.push(createNotMatchRule(regex, message, validateImmediately));
		return this;
	};

	public validate = async (model: VM): Promise<MessageResult<FN>> => {
		if (!this.condition(model)) return [];

		const value = this.selector(model);

		for (const rule of this.rules) {
			const result = await rule.validate(value, model);
			if (!result) {
				return [{ field: this.field, message: rule.message }];
			}
		}

		if (!this.validator.length) return [];

		const results: MessageResult<FN> = [];
		for (const validation of this.validator) {
			const arrValue = value as unknown[];
			for (let itemIndex = 0; itemIndex < arrValue.length; itemIndex++) {
				const arrayItem = arrValue[itemIndex];
				const id = this.arrayIdSelector ? this.arrayIdSelector(arrayItem) : itemIndex;
				const nestedResults = (await validation.validate(arrayItem)) as { field: FN; message: Message }[];
				for (const nestedResult of nestedResults) {
					results.push({
						field: `${this.field}.${id}.${nestedResult.field}` as FN,
						message: nestedResult.message,
					});
				}
			}
		}

		return results;
	};
}
